import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";

/* Shared vertical landing framework — same visual language as
   MittelstandLanding, but generalised so multiple audience-specific
   pages (agencies, enterprises, …) can share the structure by
   passing in a Copy object. */

export type UseCase = {
  tag: string;
  title: string;
  body: string;
  external?: boolean;
  link?: { label: string; href: string };
};

export type VerticalCopy = {
  eyebrow: string;
  h1a: string; h1b: string; h1hl: string; h1c: string;
  lead: string; cta: string; ctaNote: string; stackLine: string;
  stats: { num: string; label: string; src: string }[];
  loop: { title: string; running: string };
  stages: { icon: string; name: string; sub: string; gate?: boolean }[];
  gateTag: string;
  log: Record<"detect" | "trigger" | "draft" | "approve" | "execute" | "measure", string>;
  problem: {
    eyebrow: string; h2a: string; h2b: string; lead: string;
    cards: { tag: string; title: string; body: string }[];
    note1: string; note2: string;
  };
  mechanism: {
    eyebrow: string; h2: string; lead: string;
    rows: { name: string; desc: string; gate?: boolean }[];
  };
  useCases: {
    eyebrow: string; h2: string; lead: string;
    items: UseCase[];
  };
  outcome: {
    eyebrow: string; h2: string;
    cards: { ic: string; title: string; body: string }[];
  };
  mid: { eyebrow: string; h3: string; lead: string; cta: string };
  phases: {
    eyebrow: string; h2: string; lead: string;
    p: { days: string; title: string; bullets: string[] }[];
  };
  buying: { eyebrow: string; h2: string; p1: string; p2: string; pills: string[] };
  guarantee: { eyebrow: string; h2: string; body: string };
  final: {
    brand: string; h2a: string; h2b: string; h2hl: string; h2c: string;
    lead: string; cta: string; ctaNote: string; sticky: string;
  };
};

const Label = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[11px] font-mono text-primary uppercase tracking-widest mb-4">{children}</p>
);
const H2 = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-[clamp(1.5rem,3.5vw,2.25rem)] font-semibold tracking-tight leading-[1.15] mb-4">
    {children}
  </h2>
);
const Lead = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[14px] font-mono text-muted-foreground leading-relaxed max-w-2xl">{children}</p>
);
const SectionDivider = () => <div className="section-divider w-full absolute top-0" />;
const PrimaryCta = ({ href, children }: { href: string; children: React.ReactNode }) => (
  <a
    href={href}
    className="inline-flex items-center gap-1.5 h-9 px-5 rounded-md bg-primary text-primary-foreground text-[13px] font-mono font-medium hover:bg-primary/90 transition-colors"
  >
    {children}
    <ArrowRight size={13} />
  </a>
);

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" as const } },
};

const LiveLoop = ({ c }: { c: VerticalCopy }) => {
  const reduced = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduced) { setI(3); return; }
    const id = setInterval(() => setI((p) => (p + 1) % c.stages.length), 2400);
    return () => clearInterval(id);
  }, [reduced, c.stages.length]);
  const logKeys = ["detect", "trigger", "draft", "approve", "execute", "measure"] as const;

  return (
    <div className="mt-14 rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-secondary/30">
        <div className="flex items-center gap-1.5">
          <div className="h-2 w-2 rounded-full bg-border" />
          <div className="h-2 w-2 rounded-full bg-border" />
          <div className="h-2 w-2 rounded-full bg-border" />
          <span className="ml-3 text-[11px] font-mono text-muted-foreground">{c.loop.title}</span>
        </div>
        <span className="inline-flex items-center gap-1.5 font-mono text-[10.5px] text-primary">
          <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
          {c.loop.running}
        </span>
      </div>
      <div className="px-5 md:px-6 py-8">
        <div className="flex flex-col md:flex-row md:items-stretch gap-6 md:gap-0">
          {c.stages.map((s, idx) => {
            const active = idx === i;
            const done = idx < i;
            return (
              <div key={s.name} className="relative flex-1 md:text-center flex md:block items-center gap-4">
                {idx < c.stages.length - 1 && (
                  <span aria-hidden className={`hidden md:block absolute top-[22px] left-[calc(50%+22px)] right-[calc(-50%+22px)] h-px transition-colors duration-500 ${done ? "bg-primary/40" : "bg-border"}`} />
                )}
                {s.gate && (
                  <span className="hidden md:inline-block absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap font-mono text-[9.5px] uppercase tracking-widest text-primary/80">{c.gateTag}</span>
                )}
                <div className={`relative z-10 md:mx-auto h-11 w-11 rounded-lg border flex items-center justify-center font-mono text-[15px] transition-all duration-500 flex-shrink-0 ${
                  active ? "border-primary text-primary bg-primary/5"
                    : done ? "border-primary/40 text-primary/70 bg-card"
                    : "border-border text-muted-foreground bg-card"
                } ${s.gate ? "border-dashed" : ""}`}>
                  {s.icon}
                </div>
                <div>
                  <div className={`md:mt-3 font-mono text-[11px] tracking-wider transition-colors duration-500 ${active ? "text-foreground" : "text-muted-foreground"}`}>{s.name}</div>
                  <div className="mt-0.5 font-mono text-[10.5px] text-muted-foreground/60">{s.sub}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-8 rounded-md border border-border bg-secondary/20 px-4 py-3 font-mono text-[11.5px] leading-[1.9] text-muted-foreground min-h-[92px]">
          {Array.from({ length: 3 }).map((_, k) => {
            const idx = Math.max(0, i - 2) + k;
            if (idx > i) return null;
            const dim = idx < i;
            const key = logKeys[idx];
            return (
              <div key={idx} className={dim ? "opacity-40" : ""}>
                <span className="text-primary">{key.padEnd(8, " ")}</span>
                {" › "}
                {c.log[key]}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Hero = ({ c }: { c: VerticalCopy }) => (
  <header id="top" className="relative pt-32 pb-20 md:pt-40 md:pb-28">
    <div className="absolute inset-0 radial-spot pointer-events-none" />
    <div className="container max-w-5xl relative z-10">
      <p className="text-[11px] font-mono text-primary uppercase tracking-widest mb-4">{c.eyebrow}</p>
      <h1 className="text-[clamp(2rem,5vw,3.25rem)] font-semibold tracking-tight leading-[1.08] mb-6 max-w-3xl">
        <span className="block">{c.h1a}</span>
        <span className="block">{c.h1b}<span className="text-primary">{c.h1hl}</span></span>
        <span className="block">{c.h1c}</span>
      </h1>
      <p className="text-[14px] font-mono text-muted-foreground leading-relaxed max-w-xl mb-8">{c.lead}</p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <PrimaryCta href="/contact">{c.cta}</PrimaryCta>
        <span className="font-mono text-[11.5px] text-muted-foreground/60">{c.ctaNote}</span>
      </div>
      <p className="mt-6 font-mono text-[11.5px] text-muted-foreground/70">{c.stackLine}</p>
      <div className="mt-14 pt-8 border-t border-border grid gap-6 md:grid-cols-3">
        {c.stats.map((s, n) => (
          <div key={n}>
            <div className="font-mono text-[clamp(1.5rem,2.6vw,2rem)] font-semibold text-primary leading-none">{s.num}</div>
            <p className="mt-2 font-mono text-[12px] text-muted-foreground leading-relaxed">
              {s.label}
              <span className="mt-0.5 block text-[10.5px] text-muted-foreground/50">{s.src}</span>
            </p>
          </div>
        ))}
      </div>
      <LiveLoop c={c} />
    </div>
  </header>
);

const Problem = ({ c }: { c: VerticalCopy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl mb-14">
        <Label>{c.problem.eyebrow}</Label>
        <H2>{c.problem.h2a}<br /><span className="text-muted-foreground">{c.problem.h2b}</span></H2>
        <Lead>{c.problem.lead}</Lead>
      </motion.div>
      <div className="grid md:grid-cols-3 gap-4">
        {c.problem.cards.map((card, n) => (
          <motion.div key={n} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={fadeUp} className="rounded-xl border border-border bg-card p-6">
            <div className="font-mono text-[10.5px] uppercase tracking-widest text-muted-foreground/60 mb-3">{card.tag}</div>
            <h3 className="text-[14px] font-mono font-medium mb-2">{card.title}</h3>
            <p className="text-[12px] font-mono text-muted-foreground leading-relaxed">{card.body}</p>
          </motion.div>
        ))}
      </div>
      <div className="mt-10 max-w-3xl border-l-2 border-primary/50 pl-5 font-mono text-[12.5px] leading-[1.9] text-muted-foreground">
        <p>{c.problem.note1}</p>
        <p className="mt-3 text-foreground">{c.problem.note2}</p>
      </div>
    </div>
  </section>
);

const Mechanism = ({ c }: { c: VerticalCopy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl mb-12">
        <Label>{c.mechanism.eyebrow}</Label>
        <H2>{c.mechanism.h2}</H2>
        <Lead>{c.mechanism.lead}</Lead>
      </motion.div>
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {c.mechanism.rows.map((row, idx) => (
          <div key={row.name} className={`grid grid-cols-[40px_1fr] md:grid-cols-[56px_180px_1fr] gap-x-5 gap-y-1.5 items-start px-5 md:px-6 py-5 ${idx > 0 ? "border-t border-border" : ""} ${row.gate ? "bg-primary/[0.03]" : ""}`}>
            <div className="font-mono text-[12px] text-muted-foreground/70 pt-0.5">{String(idx + 1).padStart(2, "0")}{row.gate && " ▸"}</div>
            <div className={`font-mono text-[13px] font-medium ${row.gate ? "text-primary" : "text-foreground"}`}>{row.name}</div>
            <div className="col-span-2 md:col-span-1 text-[13px] font-mono text-muted-foreground leading-relaxed">{row.desc}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const UseCases = ({ c }: { c: VerticalCopy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl mb-14">
        <Label>{c.useCases.eyebrow}</Label>
        <H2>{c.useCases.h2}</H2>
        <Lead>{c.useCases.lead}</Lead>
      </motion.div>
      <div className="grid md:grid-cols-2 gap-4">
        {c.useCases.items.map((uc, n) => (
          <div key={n} className="rounded-xl border border-border bg-card p-6 flex flex-col">
            <div className="font-mono text-[10.5px] uppercase tracking-widest text-muted-foreground/60 mb-3">{uc.tag}</div>
            <h3 className="text-[14px] font-mono font-medium mb-2">{uc.title}</h3>
            <p className="text-[12px] font-mono text-muted-foreground leading-relaxed flex-1">{uc.body}</p>
            {uc.link && (
              <a
                href={uc.link.href}
                target={uc.external ? "_blank" : undefined}
                rel={uc.external ? "noopener noreferrer" : undefined}
                className="mt-4 inline-flex items-center gap-1.5 font-mono text-[12px] text-primary hover:text-primary/80 transition-colors"
              >
                {uc.link.label}
                {uc.external ? <ArrowUpRight size={12} /> : <ArrowRight size={12} />}
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Outcome = ({ c }: { c: VerticalCopy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl mb-14">
        <Label>{c.outcome.eyebrow}</Label>
        <H2>{c.outcome.h2}</H2>
      </motion.div>
      <div className="grid md:grid-cols-3 gap-4">
        {c.outcome.cards.map((card, n) => (
          <div key={n} className="rounded-xl border border-border bg-card p-6">
            <div className="h-8 px-2 inline-flex items-center rounded-md bg-primary/10 font-mono text-[11px] font-medium text-primary mb-4">{card.ic}</div>
            <h3 className="text-[14px] font-mono font-medium mb-2">{card.title}</h3>
            <p className="text-[12px] font-mono text-muted-foreground leading-relaxed">{card.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const MidCTA = ({ c }: { c: VerticalCopy }) => (
  <section className="py-16 md:py-20 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 rounded-xl border border-border bg-card px-6 py-6 md:px-8 md:py-7">
        <div className="max-w-xl">
          <p className="text-[11px] font-mono text-primary uppercase tracking-widest mb-2">{c.mid.eyebrow}</p>
          <h3 className="text-[clamp(1.15rem,2.2vw,1.5rem)] font-semibold tracking-tight leading-snug mb-2">{c.mid.h3}</h3>
          <p className="text-[13px] font-mono text-muted-foreground leading-relaxed">{c.mid.lead}</p>
        </div>
        <div className="shrink-0"><PrimaryCta href="/contact">{c.mid.cta}</PrimaryCta></div>
      </div>
    </div>
  </section>
);

const Phases = ({ c }: { c: VerticalCopy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl mb-14">
        <Label>{c.phases.eyebrow}</Label>
        <H2>{c.phases.h2}</H2>
        <Lead>{c.phases.lead}</Lead>
      </motion.div>
      <div className="grid md:grid-cols-3 gap-4">
        {c.phases.p.map((ph, n) => (
          <div key={n} className="rounded-xl border border-border bg-card p-6">
            <div className="font-mono text-[10.5px] tracking-widest text-primary mb-3">{ph.days}</div>
            <h3 className="text-[14px] font-mono font-medium mb-3">{ph.title}</h3>
            <ul className="space-y-1.5">
              {ph.bullets.map((b, i) => (
                <li key={i} className="relative pl-4 text-[12px] font-mono text-muted-foreground leading-relaxed">
                  <span aria-hidden className="absolute left-0 top-[7px] h-1 w-1 rounded-sm bg-primary/70" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Buying = ({ c }: { c: VerticalCopy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl">
        <Label>{c.buying.eyebrow}</Label>
        <H2>{c.buying.h2}</H2>
        <p className="text-[14px] font-mono text-muted-foreground leading-relaxed max-w-2xl mb-4">{c.buying.p1}</p>
        <p className="text-[14px] font-mono text-foreground leading-relaxed max-w-2xl mb-6">{c.buying.p2}</p>
        <div className="flex flex-wrap gap-2">
          {c.buying.pills.map((p) => (
            <span key={p} className="inline-flex rounded-md border border-primary/30 bg-primary/[0.06] px-3 py-1 font-mono text-[11.5px] text-primary">{p}</span>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

const Guarantee = ({ c }: { c: VerticalCopy }) => (
  <section className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl">
      <div className="rounded-xl border border-primary/30 bg-card p-8 md:p-12 text-center max-w-3xl mx-auto">
        <Label>{c.guarantee.eyebrow}</Label>
        <h2 className="text-[clamp(1.5rem,3.5vw,2.25rem)] font-semibold tracking-tight leading-[1.15] mb-4">{c.guarantee.h2}</h2>
        <p className="text-[14px] font-mono text-muted-foreground leading-relaxed max-w-xl mx-auto">{c.guarantee.body}</p>
      </div>
    </div>
  </section>
);

const FinalCTA = ({ c }: { c: VerticalCopy }) => (
  <section id="apply" className="py-24 md:py-32 relative">
    <SectionDivider />
    <div className="container max-w-5xl text-center">
      <p className="text-[11px] font-mono text-primary uppercase tracking-widest mb-4">{c.final.brand}</p>
      <h2 className="mx-auto max-w-2xl text-[clamp(1.75rem,4vw,2.5rem)] font-semibold tracking-tight leading-[1.12] mb-4">
        {c.final.h2a}<br />{c.final.h2b}<span className="text-primary">{c.final.h2hl}</span>{c.final.h2c}
      </h2>
      <p className="mx-auto max-w-xl text-[14px] font-mono text-muted-foreground leading-relaxed mb-8">{c.final.lead}</p>
      <div className="flex items-center justify-center"><PrimaryCta href="/contact">{c.final.cta}</PrimaryCta></div>
      <p className="mt-4 font-mono text-[11.5px] text-muted-foreground/60">{c.final.ctaNote}</p>
    </div>
  </section>
);

const StickyPill = ({ c }: { c: VerticalCopy }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 800);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className={`fixed bottom-5 right-5 z-40 transition-all duration-300 ${visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none"}`}>
      <a href="/contact" className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-primary text-primary-foreground text-[12.5px] font-mono font-medium shadow-lg shadow-primary/20 hover:bg-primary/90 transition-colors">
        {c.final.sticky}
        <ArrowRight size={13} />
      </a>
    </div>
  );
};

const VerticalLanding = ({ c }: { c: VerticalCopy }) => (
  <>
    <Hero c={c} />
    <Problem c={c} />
    <Mechanism c={c} />
    <UseCases c={c} />
    <Outcome c={c} />
    <MidCTA c={c} />
    <Phases c={c} />
    <Buying c={c} />
    <Guarantee c={c} />
    <FinalCTA c={c} />
    <StickyPill c={c} />
  </>
);

export default VerticalLanding;
